<script>
import axios from 'axios';
import { defineComponent, ref, computed, onMounted, inject, watch } from 'vue'
import HierarchyTreePicker from "@/components/cHierarchyTreePicker.vue"
import DocSummary from "@/components/common/DocViewPopUp"
import DocumentCompare from "@/components/document/DocumentCompare";
import utils from '@/common/utils';
import { LEVELLING_ANCHOR, LEVELLING_ANCHOR_FORCED_NOTE, shortName } from '@rm-ui/levelling/levellingMeta';

export default defineComponent({
    name: 'EvaluationCompare',
    props: {
        doc: {
            type: Object,
            required: false,
            default: null
        },
        // Standalone mode: no current document (opened from the jobs grid).
        // Drops the "current role" anchoring and always shows both tabs.
        standalone: {
            type: Boolean,
            default: false
        },
        // Standalone seed set — the jobs to compare (e.g. the doc_ids Atlas
        // identified). Included in every comparison query as a base set the
        // hierarchy/discipline/level pickers then refine.
        docIds: {
            type: Array,
            default: () => []
        },
        evaluation: {
            type: Object,
            required: false
        },
        functions: {
            type: Array,
            requited: true
        },
        tracks: {
            type: Array,
            required: true
        },
        factors: {
            type: Array,
            required: true
        },
        levels: {
            type: Array,
            required: true
        },
        // Current evaluation's level as it reads in the chosen track (e.g. "O4"),
        // derived by the holder from the selected level + function/track pairing.
        selectedLevelAbbreviation: {
            type: String,
            default: null
        },
        // Which tab to open on: 'compare' (default) or 'breakdown'. Lets the
        // summary's Factor Breakdown toggle jump straight to the level breakdown.
        initialTab: {
            type: String,
            default: 'compare'
        }
    },
    emits: ['back', 'openDocument', 'copyDocument', 'compareDocuments'],
    components: {
        HierarchyTreePicker,
        DocSummary,
        DocumentCompare,
    },
    setup(props, { emit }) {

        const loading = ref(true)
        const docs = ref([])
        // Ceiling scores for the standard flow — the highest each factor / the
        // overall total can reach. Framework-derived, so constant across docs;
        // returned once at the top level of the comparison payload.
        const factorMaxScores = ref({})
        const totalMaxScore = ref(0)
        const activeTab = ref(props.initialTab === 'breakdown' ? 'breakdown' : 'compare')
        //const loginState = inject('loginState')
        const store = inject('store')
        const hierarchiesLoading = computed(() => store.state.hierarchies.loading)
        const hierarchies = computed(() => store.state.hierarchies.hierarchies)
        const documentSummary = ref({
            trigger: null,
            doc_id: null,
        })
        const documenCompare = ref({
            show: false,
            ids: props.doc ? [props.doc.doc_id] : [],
            docs: [],
        });
        const evaluationTypesSelected = ref([]);
        const evaluationTypesAvailable = ref([]);
        const selectedHierarchyNodes = ref([])
        //const selectedTemplate = computed(() => loginState.user.createDocumentDialogOptions.find(dt => dt.tmpl_name === props.doc.tmpl_name) || {})
        const applicableHierarchyTypes = ref([])
        const selectedFunctions = ref([])
        const selectedLevels = ref([])
        const selectedComparisonCount = computed(() => documenCompare.value.ids.length)

        // Current evaluation's total score, shown beside the tab toggle so the score
        // and level stay visible while comparing against other roles. Classification
        // evaluations carry no score, and standalone mode has no current evaluation
        // at all, so both cases resolve to null and the stat is hidden.
        const currentScore = computed(() => {
            const score = props.evaluation?.score
            if (score === null || score === undefined || score === '') return null
            const rounded = Math.round(Number(score))
            return Number.isFinite(rounded) ? rounded : null
        })

        const compareTracks = computed(() => {
            return Array.from(new Set(docs.value.map(x => x.track)))
                .map(t => props.tracks.find(pt => pt.name === t) || t)
        })
        /* Anchor tooltip. A FORCED row is present because the server always includes
           the family's calibration reference, so it may not satisfy the user's level
           filter — say so, or it reads as the filter being broken. Copy is shared so
           both portals explain it identically. */
        const anchorTip = (d) => [
            d.isCeiling
                ? 'CEO ceiling — this anchor fixes the top of your framework.'
                : 'Anchor role — the evaluated reference this family calibrates against.',
            d.isForcedAnchor ? LEVELLING_ANCHOR_FORCED_NOTE : '',
        ].filter(Boolean).join(' ')

        const compareLevels = computed(() => {
            // Anchor roles (PD-1258): the anchor leads its cell as the calibration
            // reference — the CEO ceiling first, then anchors, then everything else
            // in the server's order (level/function/track/name).
            const anchorRank = (d) => (d.isCeiling ? 2 : d.isAnchor ? 1 : 0)
            return Array.from(new Set(docs.value.map(x => x.level)))
                .map(l => ({
                    label: l,
                    tracks: props.tracks.reduce((acc, t) => (acc[t.name] = (
                        docs.value
                            .filter(d => d.level === l && d.track === t.name)
                            .slice()
                            .sort((a, b) => anchorRank(b) - anchorRank(a))
                    ), acc), {})
                }))
        })
        const breakdownDocs = computed(() => {
            return docs.value.filter(d => evaluationTypesSelected.value.some(ct => ct === d.flowType));
        })
        const hasResults = computed(() => activeTab.value === 'compare'
            ? compareLevels.value.length > 0
            : breakdownDocs.value.length > 0)

        function getSelectedTrackText(item, index) {
            if (item && index > 0) return "";
            if (selectedFunctions.value.length === props.functions.length)
                return selectedFunctions.value.length > 0 ? "All" : "None";
            else if (selectedFunctions.value.length > 2)
                return `${selectedFunctions.value.length} selected`

            return selectedFunctions.value.map((i) => i.name).join(", ");
        }

        function getSelectedLevelText(item, index) {
            if (item && index > 0) return "";
            if (selectedLevels.value.length === props.levels.length)
                return selectedLevels.value.length > 0 ? "All" : "None";
            else if (selectedLevels.value.length > 2)
                return `${selectedLevels.value.length} selected`

            return selectedLevels.value.map((i) => i.name).join(", ");
        }

        onMounted(async () => {
            if (!hierarchiesLoading.value) {
                initialize()
                loadDocs(true)
            }
        })

        watch(hierarchiesLoading, (newVal) => {
            if (!newVal) {
                initialize()
                loadDocs(true)
            }
        })

        watch(selectedHierarchyNodes, () => {
            loadDocs(false)
        }, { deep: true })
        watch(selectedFunctions, () => {
            loadDocs(false)
        })
        watch(selectedLevels, () => {
            loadDocs(false)
        }, { deep: true })

        async function initialize() {
            applicableHierarchyTypes.value = hierarchies.value
                //.filter((x) => !selectedTemplate.value?.hiddenHierarchyTypes?.some((h) => h === x.ht_id))
                .filter((x) => ['job', 'business org'].includes(x.ht_name.toLowerCase()))
                .map((ht) => {
                    const local = { ...ht, lostFocus: false };
                    return local;
                });

        }

        async function loadDocs(init) {
            axios.post(`/document/getDocumentLfEvaluationComparison`, {
                doc_id: props.doc?.doc_id ?? null,
                init: init,
                tmpl_id: props.doc?.tmpl_id ?? null,
                docIds: props.docIds,
                hierarchyNodeIds: selectedHierarchyNodes.value,
                functionIds: selectedFunctions.value.map(f => f.id),
                levelIds: selectedLevels.value.map(l => l.id),
            })
            .then(response => {
                if (response.data.Status === 'OK') {
                    let docList = response.data.Data.docs || [];
                    const curDoc = docList.find(x => x.doc_id === props.doc?.doc_id);
                    if (curDoc) curDoc.isCurrentDoc = true;
                    docs.value = docList;
                    factorMaxScores.value = response.data.Data.factorMaxScores || {};
                    totalMaxScore.value = response.data.Data.totalMaxScore || 0;
                    evaluationTypesAvailable.value = [];
                    if (docList.some(d => d.flowType === "standard")) evaluationTypesAvailable.value.push({ text: "Evaluations", value: "standard"});
                    if (docList.some(d => d.flowType === "classification")) evaluationTypesAvailable.value.push({ text: "Classifications", value: "classification"});

                    if (init) {
                        selectedHierarchyNodes.value = response.data.Data.hierarchyNodeIds || [];
                        selectedFunctions.value = props.functions.filter(f => (response.data.Data.functionIds || []).includes(f.id));
                        selectedLevels.value = props.levels.filter(l => response.data.Data.levelIds?.includes(l.id));

                        const findTreePickerId = (node_id, options) => {
                            if (!options || options.length === 0)
                            return null;

                            const opt = options.find(o => o.node_id === node_id);
                            if (opt)
                                return opt.id;
                            else {
                                for (let i = 0; i < options.length; i++) {
                                    const id = findTreePickerId(node_id, options[i].children);
                                    if (id)
                                    return id;
                                }
                            }
                        }

                        (response.data.Data.hierarchyNodeIds || []).forEach(x => {
                            const ht = applicableHierarchyTypes.value.find(h => h.ht_id === x.ht_id);
                            if (ht && (!ht.treePickerValue || ht.treePickerValue.length === 0)) {
                                const nodeIds = x.node_ids || [];
                                const pickerValues = nodeIds.map(node_id => findTreePickerId(node_id, ht.treePickerOptionsActive)).filter(id => id);
                                ht.treePickerValue = pickerValues;
                            }
                        });

                        evaluationTypesSelected.value = 
                            !curDoc ? evaluationTypesAvailable.value.map(x => x.value) :
                            curDoc.flowType === "classification" ?
                            ["classification"] :
                            ["standard"];
                    }
                }
                loading.value = false
            })
            .catch(error => {
                console.error('Error fetching comparison data:', error)
                loading.value = false
            })
        }

        function onHryNodeIdSelect(ht, selNodeIds) {
            const idx = selectedHierarchyNodes.value.findIndex((x) => x.ht_id === ht.ht_id);
            const newValue = {
                ht_id: ht.ht_id,
                node_ids: selNodeIds,
            }
            if (idx > -1) {
                selectedHierarchyNodes.value.splice(idx, 1, newValue);
            } else {
                selectedHierarchyNodes.value.push(newValue);
            }
        }

        function openDocumentSummary(event, item) {
            documentSummary.value.doc_id = item.doc_id;
            documentSummary.value.trigger = event;
        }

        function compareDocuments() {
            if (documenCompare.value.ids.length < 2) return;
            documenCompare.value.show = true;
            documenCompare.value.docs = docs.value.filter(x => documenCompare.value.ids.some(i => i === x.doc_id));
        }

        function clearSelection() {
            documenCompare.value.ids = [];
        }

        function formatScore(score) {
            return Math.round(Number(score) || 0)
        }

        // Breakdown rows show the same track-coloured square beside the level code
        // as the compare-view role cards. Docs only carry the track name, so resolve
        // back to the track for its abbreviation and colour.
        function docTrack(d) {
            return props.tracks.find(t => t.name === d.track);
        }

        function trackAbbr(d) {
            const track = docTrack(d);
            return (track?.abbreviation || track?.name || d.track || '').charAt(0).toUpperCase();
        }

        // Tracks without a configured colour fall back to the stylesheet default.
        function trackSquareStyle(d) {
            const colour = docTrack(d)?.backgroundColour;
            return colour ? { backgroundColor: utils.shadeColor(colour, 0, -50) } : {};
        }

        // Standard-flow score for a breakdown cell. Only appends the framework
        // ceiling ("score / max") when more than one evaluation type is on
        // screen — with a single type the bare score is enough.
        function scoreDisplay(value, max) {
            const score = formatScore(value);
            return evaluationTypesSelected.value.length > 1
                ? `${score} / ${formatScore(max)}`
                : `${score}`;
        }

        return {
            LEVELLING_ANCHOR,
            anchorTip,
            props,
            loading,
            selectedHierarchyNodes,
            applicableHierarchyTypes,
            hierarchiesLoading,
            selectedFunctions,
            getSelectedTrackText,
            getSelectedLevelText,
            onHryNodeIdSelect,
            currentScore,
            compareTracks,
            compareLevels,
            breakdownDocs,
            hasResults,
            docs,
            activeTab,
            documentSummary,
            documenCompare,
            selectedLevels,
            factorMaxScores,
            totalMaxScore,
            selectedComparisonCount,
            compareDocuments,
            clearSelection,
            openDocumentSummary,
            formatScore,
            scoreDisplay,
            trackAbbr,
            trackSquareStyle,
            shadeColor: utils.shadeColor,
            shortName,
            evaluationTypesSelected,
            evaluationTypesAvailable,
        }
    }
})
</script>

<template>
    <div class="lvl-eval">
        <v-container v-if="loading || hierarchiesLoading" class="fill-height">
            <v-row justify="center" align="center">
                <v-col cols="auto" class="text-center">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <p class="mt-4 text-h6">Loading comparison data...</p>
                </v-col>
            </v-row>
        </v-container>

        <div v-else class="lvl-card cmp-card">
            <div class="cmp-in">
                <!-- Header: title + Summary / Compare / Factor Breakdown toggle -->
                <div class="lvl-sum-head">
                    <div v-if="standalone" class="lvl-sum-head-t">
                        <h1 class="lvl-sum-title">{{ "Compare Levels for " + breakdownDocs.length + " Selected Roles" }}</h1>
                    </div>
                    <div v-else class="lvl-sum-head-t">
                        <div v-if="evaluation && evaluation.status === 'DRAFT'" class="lvl-sum-eyebrow">Review and save</div>
                        <h1 class="lvl-sum-title">Compare roles</h1>
                    </div>
                    <div class="d-flex align-center">
                        <div
                            v-if="currentScore !== null"
                            class="lvl-sum-head-stat"
                        >
                            <span v-if="currentScore !== null"><strong>{{ currentScore }}</strong> points</span>
                            <span v-if="currentScore !== null && selectedLevelAbbreviation" aria-hidden="true">·</span>
                            <strong v-if="selectedLevelAbbreviation">{{ selectedLevelAbbreviation }}</strong>
                        </div>
                        <div class="lvl-sum-tabs">
                            <button v-if="!standalone" type="button" @click="$emit('back')">
                                <span class="ms">summarize</span> Summary
                            </button>
                            <button type="button" :class="{ on: activeTab === 'compare' }" @click="activeTab = 'compare'">
                                <span class="ms">compare_arrows</span> Compare
                            </button>
                            <button type="button" :class="{ on: activeTab === 'breakdown' }" @click="activeTab = 'breakdown'">
                                <span class="ms">bar_chart</span> Factor Breakdown
                            </button>
                        </div>
                        <v-btn v-if="standalone" @click="$emit('close')" icon
                                class="btn-background cmp-close-btn">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>

                <div v-if="activeTab === 'compare'" class="lvl-sum-desc">
                    Where this role sits against other levelled roles in the organisation, by track and band. Showing Brand Management by default - clear the filter for the whole organisation.
                </div>
                <div v-else-if="activeTab === 'breakdown'" class="lvl-sum-desc">
                    How each role’s score breaks down across the factor groups - compare the shape, not just the headline score. Showing Brand Management by default - clear the filter for the whole organisation.
                </div>

                <!-- Compare Profiles action + filters -->
                <div class="cmp-bar">
                    <div class="cmp-filters" v-if="doc && applicableHierarchyTypes.length > 0">
                        <div class="filter-item" v-for="(h) in applicableHierarchyTypes" :key="h.ht_id">
                            <HierarchyTreePicker :id="`evaluateNew_${h.ht_name}`"
                                v-model="h.treePickerValue"
                                :options="h.treePickerOptionsActive"
                                :fulldetail="true"
                                :label="h.label"
                                placeholder="Select..."
                                :clearable="true"
                                :hierarchyType="h"
                                :multiple="true"
                                :limit="1"
                                dense
                                @changeNodeIds="onHryNodeIdSelect(h, $event)"
                            >
                            </HierarchyTreePicker>
                        </div>
                        <div class="filter-item filter-item-functions">
                            <v-select
                                label="Disciplines"
                                placeholder="Select..."
                                v-model="selectedFunctions"
                                :items="functions"
                                item-text="name"
                                item-value="id"
                                return-object
                                multiple
                                outlined
                                dense
                                class="roundish"
                                hide-details
                                clearable
                                :menu-props="{
                                    closeOnContentClick: false,
                                    maxHeight: '400',
                                    'offset-y': true,
                                }"
                                >
                                <template v-slot:selection="{ item, index }">
                                    {{ getSelectedTrackText(item, index) }}
                                </template>
                            </v-select>
                        </div>
                        <div class="filter-item filter-item-levels">
                            <v-select
                                label="Levels"
                                placeholder="Select..."
                                v-model="selectedLevels"
                                :items="levels"
                                item-text="name"
                                item-value="id"
                                return-object
                                multiple
                                outlined
                                dense
                                class="roundish"
                                hide-details
                                clearable
                                :menu-props="{
                                    closeOnContentClick: false,
                                    maxHeight: '400',
                                    'offset-y': true,
                                }"
                                >
                                <template v-slot:selection="{ item, index }">
                                    {{ getSelectedLevelText(item, index) }}
                                </template>
                            </v-select>
                        </div>
                    </div>
                    <div v-else class="cmp-filters"></div>

                    <button
                        type="button"
                        :class="[ 'lvl-btn', 'cmp-compare-btn', selectedComparisonCount < 2 ? 'lvl-btn--outlined' : '' ]"
                        :disabled="selectedComparisonCount < 2"
                        @click="compareDocuments"
                    >
                        <span class="ms">difference</span>
                        Compare
                        <span v-if="selectedComparisonCount > 1">({{ selectedComparisonCount }})</span>
                        <!-- <span
                            v-if="selectedComparisonCount >= 2"
                            class="ms cmp-clear"
                            title="Clear selection"
                            @click.stop="clearSelection"
                        >close</span> -->
                    </button>
                </div>

                <div class="table-container lvl-scroll">

                    <table v-if="activeTab === 'compare' && compareLevels.length" class="compare-table">
                        <colgroup>
                            <col width="100px" />
                        </colgroup>
                        <thead>
                            <!-- Track row -->
                            <tr>
                                <th class="level-header">Level</th>
                                <th v-for="t in tracks" :key="t.id" class="track-header">
                                    <span class="track-name">{{ t.name }}</span>
                                    <span class="track-abbr">{{ t.abbreviation || t.name.charAt(0).toUpperCase() }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="level in compareLevels" :key="level.label">
                                <td class="level-cell">{{ level.label }}</td>
                                <td v-for="t in tracks" :key="t.id" class="role-cell">
                                    <div v-for="d in level.tracks[t.name] || []" :key="d.doc_id"
                                        class="role-card"
                                        :class="{ 'role-card--current': d.doc_id === doc?.doc_id, 'role-card--anchor': !!d.isAnchor }">
                                        <div class="role-name-row">
                                            <a href="#" @click.prevent="openDocumentSummary($event, d)" class="role-name">{{ d.doc_name }}</a>
                                            <!-- Anchor roles (PD-1258): the evaluated reference this cell calibrates against. -->
                                            <span
                                                v-if="d.isAnchor"
                                                class="rc-anchor"
                                                :title="anchorTip(d)"
                                                :class="{ 'is-ceiling': d.isCeiling }"
                                            ><span class="ms" style="font-size:12px">{{ d.isCeiling ? LEVELLING_ANCHOR.ceiling.icon : LEVELLING_ANCHOR.anchor.icon }}</span> {{ d.isCeiling ? LEVELLING_ANCHOR.ceiling.short : LEVELLING_ANCHOR.anchor.short }}</span>
                                            <span v-if="d.doc_id === doc?.doc_id" class="your-role-badge">Your role</span>
                                            <input
                                                v-else
                                                type="checkbox"
                                                class="role-compare-checkbox"
                                                title="select for document comparison"
                                                :value="d.doc_id"
                                                v-model="documenCompare.ids"
                                            />
                                        </div>
                                        <div class="role-badges">
                                            <span class="lvl-badge">
                                                <span class="lvl-badge-sq" :style="{ backgroundColor: shadeColor(t.backgroundColour, 0, -50) }">{{ (t.abbreviation || t.name).charAt(0).toUpperCase() }}</span>
                                                <span class="lvl-badge-txt">{{ d.abbreviation }}</span>
                                            </span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                        <table v-else-if="activeTab === 'breakdown' && breakdownDocs.length" class="breakdown-table">
                            <colgroup>
                                <col width="280px" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th class="role-header">Jobs</th>
                                    <th class="score-header">
                                        {{ evaluationTypesSelected.some(x => x === 'standard') ? 'Score' : 'Level' }}
                                    </th>
                                    <th v-for="f in factors" :key="f.factorId" class="factor-header">
                                        {{ shortName(f.name) }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="d in breakdownDocs" :key="d.doc_id" :class="{ 'role--current': d.doc_id === doc?.doc_id }">
                                   <td class="breakdown-role-cell">
                                        <div v-if="d.doc_id === doc?.doc_id" class="your-role">Your job</div>
                                        <div class="role-name-row">
                                            <!-- Anchor roles (PD-1258): LEADS the row, as in the design
                                                 prototype — an accent label before the name, so the
                                                 calibration references are picked out while scanning down
                                                 the Jobs column rather than found at the end of each title. -->
                                            <span
                                                v-if="d.isAnchor"
                                                class="breakdown-anchor"
                                                :title="anchorTip(d)"
                                            >
                                                <span class="ms">{{ d.isCeiling ? LEVELLING_ANCHOR.ceiling.icon : LEVELLING_ANCHOR.anchor.icon }}</span>
                                                {{ d.isCeiling ? LEVELLING_ANCHOR.ceiling.label : LEVELLING_ANCHOR.anchor.short }}
                                            </span>
                                            <a href="#" @click.prevent="openDocumentSummary($event, d)" class="role-name">{{ d.doc_name }}</a>
                                            <input
                                                v-if="d.doc_id !== doc?.doc_id"
                                                type="checkbox"
                                                class="role-compare-checkbox"
                                                title="select for document comparison"
                                                :value="d.doc_id"
                                                v-model="documenCompare.ids"
                                            />
                                        </div>
                                    </td>
                                    <td class="breakdown-score-cell">
                                        <template v-if="d.flowType === 'standard'">
                                            {{ scoreDisplay(d.scores?.total, totalMaxScore) }}
                                        </template>
                                        <template v-else-if="d.flowType === 'classification'">
                                            <span class="lvl-badge lvl-badge--boxed">
                                                <span class="lvl-badge-sq" :style="trackSquareStyle(d)">{{ trackAbbr(d) }}</span>
                                                <span class="lvl-badge-txt">{{ d.abbreviation }}</span>
                                            </span>
                                        </template>
                                    </td>
                                    <td v-for="f in factors" :key="f.id" class="factorlevel-cell">
                                        {{ d.flowType === 'standard' ?
                                            scoreDisplay(d.scores?.factors?.[f.id]?.score, factorMaxScores[f.id]) : d.factors[f.id] }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                    <div v-if="!hasResults" class="lvl-empty">
                        <div class="ic"><span class="ms">search_off</span></div>
                        <h3>No matching jobs</h3>
                        <p>No jobs to display which match this filter selection</p>
                    </div>

                </div>
            </div>
        </div>

        <DocSummary
            :doc_id="documentSummary.doc_id"
            :trigger="documentSummary.trigger"
            @openDocument="$emit('openDocument', $event)"
            @copyDocument="$emit('copyDocument', $event)"
        ></DocSummary>
        <DocumentCompare
            :show="documenCompare.show"
            :compareList="documenCompare.docs"
            :highlightChanges="false"
            multiDoc
            showWorkflowActions
            @openDocument="$emit('openDocument', $event)"
            @copyDocument="$emit('copyDocument', $event)"
            @nodata="documenCompare.show = false"
            @close="documenCompare.show = false"
            >
        </DocumentCompare>
    </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>

<style scoped lang="scss">
/* ---- surface ---- */
.cmp-card {
    padding: 0;
}

.cmp-in {
    padding: 28px 32px 22px;
    display: flex;
    flex-direction: column;
}

.cmp-close-btn {
    width: 38px !important;
    height: 38px !important;
    margin-left: 10px;
}

/* Current score / level, sat to the left of the tab toggle. Colours and tabular
   numerals come from .lvl-sum-head-stat in the shared theme; only the layout and
   the gap from the tabs are local. */
.lvl-sum-head-stat {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-right: 16px;
}

/* ---- loading ---- */
.cmp-loading, .compare-title-row-dialog {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 80px 24px;
    color: var(--v3-text-2);
.compare-title-row-dialog {
    margin-top: 16px;
}
    p {
        margin: 0;
        font-size: 14px;
    }
}

.cmp-spinner {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 3px solid var(--v3-border);
    border-top-color: var(--v3-accent);
    animation: cmp-spin 0.8s linear infinite;
}

@keyframes cmp-spin {
    to { transform: rotate(360deg); }
}

.cmp-compare-btn {
    flex: 0 0 auto;
}

/* ---- tabs + filters bar ---- */
.cmp-bar {
    display: flex;
    gap: 24px;
    border-bottom: 1px solid var(--v3-border);
    margin-bottom: 20px;
}

.cmp-filters {
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 12px;
    padding-bottom: 8px;
}

.filter-item {
    flex: 0 1 280px;
    min-width: 0;
    &.filter-item-functions,
    &.filter-item-levels {
        flex: 0 1 150px;
    }
}

.filter-item > * {
    width: 100%;
}

/* Reskin the Vuetify outlined selects + tree picker to v3 tokens */
.cmp-filters ::v-deep {
    .v-input {
        font-family: var(--font-sans);
        font-size: 13.5px;
    }

    .v-input__slot {
        background: var(--v3-surface) !important;
        border-radius: var(--v3-r-sm) !important;
    }

    fieldset {
        border-color: var(--v3-border-strong) !important;
        border-radius: var(--v3-r-sm) !important;
    }

    .v-input--is-focused fieldset {
        border-color: var(--v3-accent) !important;
        border-width: 1px !important;
    }

    .v-label {
        color: var(--v3-text-3) !important;
        font-size: 13.5px;
    }

    .v-label--active {
        color: var(--v3-text-2) !important;
    }

    .v-select__selection,
    .v-select__selections,
    input {
        color: var(--v3-text) !important;
    }

    .v-icon {
        color: var(--v3-text-3) !important;
    }
}

/* ---- tables ---- */
.table-container {
    max-height: calc(100vh - 350px);
    overflow: auto;
    border: 1px solid var(--v3-border);
    border-radius: var(--v3-r-md);
    background: var(--v3-surface);
}

.compare-table, .breakdown-table {
    width: 100%;
    border-spacing: 0;
    table-layout: fixed;
    font-size: 13.5px;
    background: var(--v3-surface);

    /* Sticky header row: uniform light surface, sentence-case muted labels. */
    thead {
        position: sticky;
        top: 0;
        z-index: 2;
    }

    thead th {
        border-bottom: 1px solid var(--v3-border);
        color: var(--v3-text-3);
        background: var(--v3-surface);
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0;
        text-transform: none;
        padding: 14px 16px;
        vertical-align: bottom;
        text-align: left;
    }

    td {
        border-bottom: 1px solid var(--v3-border-soft);
        padding: 12px 16px;
        vertical-align: top;
        text-align: left;
        color: var(--v3-text);
    }

    tbody tr:last-child td {
        border-bottom: 0;
    }
}

/* =========================== Compare table =========================== */
.compare-table {
    thead th {
        background: var(--v3-surface-2);
    }
    /* subtle column separators */
    th, td {
        border-right: 1px solid var(--v3-border-soft);
    }
    th:last-child, td:last-child {
        border-right: 0;
    }

    .track-name {
        display: block;
        font-size: 13px;
        font-weight: 700;
        color: var(--v3-text);
    }

    .track-abbr {
        display: block;
        margin-top: 2px;
        font-size: 12px;
        font-weight: 600;
        color: var(--v3-text-3);
    }

    /* left "Level" column */
    .level-cell {
        background: var(--v3-surface-2);
        text-align: center;
        font-size: 15px;
        font-weight: 700;
        color: var(--v3-text);
        vertical-align: middle;
    }

    .role-cell {
        padding: 10px;
        vertical-align: top;
    }

    /* Role cards */
    .role-card {
        background: var(--v3-surface);
        border: 1px solid var(--v3-border);
        border-radius: var(--v3-r-md);
        padding: 12px 14px;
    }

    .role-card + .role-card {
        margin-top: 10px;
    }

    .role-card--current {
        border-color: var(--v3-accent);
        box-shadow: 0 0 0 1px var(--v3-accent);
        background: color-mix(in srgb, var(--v3-accent) 6%, var(--v3-surface));
    }

    /* Anchor roles (PD-1258): a quiet left spine, so the anchor reads as the
       cell's reference without competing with "Your role". */
    .role-card--anchor {
        border-color: color-mix(in srgb, var(--v3-accent) 45%, transparent);
        box-shadow: inset 3px 0 0 var(--v3-accent);
    }

    .rc-anchor {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        flex: none;
        font-size: 10px;
        font-weight: 700;
        color: var(--v3-accent);
        cursor: help;
    }

    .role-name-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 10px;
    }


    .role-name {
        /* Takes the slack so the checkbox stays pinned right. */
        margin-right: auto;
        font-size: 13.5px;
        font-weight: 700;
        color: var(--v3-text);
        text-decoration: none;
        line-height: 1.35;

        &:hover {
            text-decoration: underline;
        }
    }

    .role-badges {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }
}

/* Level badge: colour-coded track square + level code. Shared by the cards. */
.lvl-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
}

/* Pill-wrapped variant, used where the badge sits directly on a table row
   rather than inside a role card. */
.lvl-badge--boxed {
    background: var(--v3-surface-2);
    border: 1px solid var(--v3-border-soft);
    border-radius: var(--v3-r-md);
    padding: 3px 9px 3px 4px;
}

.lvl-badge-sq {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 5px;
    background: var(--v3-text-3);
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    flex: 0 0 auto;
}

.lvl-badge-txt {
    font-size: 13px;
    font-weight: 700;
    color: var(--v3-text);
}

.role-compare-checkbox {
    flex: 0 0 auto;
    margin-top: 2px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--v3-accent);
}

.your-role-badge {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 600;
    color: var(--v3-accent);
    background: color-mix(in srgb, var(--v3-accent) 12%, var(--v3-surface));
    border-radius: 999px;
    padding: 2px 9px;
    white-space: nowrap;
}

/* ========================= Breakdown table ========================= */
.breakdown-table {
    .role-header,
    .score-header,
    .factor-header {
        font-weight: 700;
        color: var(--v3-text-2);
        padding-top: 20px;
        padding-bottom: 8px;
    }
    .score-header,
    .factor-header,
    .breakdown-score-cell,
    .factorlevel-cell {
        text-align: right;
    }

    .breakdown-role-cell {
        vertical-align: middle;
    }

    .your-role {
        font-size: 11px;
        font-weight: 500;
        color: var(--v3-text);
        margin-bottom: 2px;
    }

    /* Left-packed, NOT space-between: the row is [anchor label] [name] [checkbox],
       and space-between would throw the name to the far side of the column away
       from its own label. The name takes the slack so the checkbox stays right. */
    .role-name-row {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
    }

    /* The anchor label leading the row: same size/weight as the "Your job" marker
       above it, so the two read as one family of row markers. */
    .breakdown-anchor {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 11px;
        font-weight: 600;
        color: var(--v3-accent);
        white-space: nowrap;
    }
    .breakdown-anchor .ms { font-size: 13px; line-height: 1; }

    .role-name {
        font-size: 13.5px;
        font-weight: 700;
        color: var(--v3-text);
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    .breakdown-score-cell {
        font-size: 14px;
        font-weight: 700;
        color: var(--v3-text);
        vertical-align: middle;
    }

    .factorlevel-cell {
        color: var(--v3-text);
        font-weight: 500;
        vertical-align: middle;
    }

    tbody tr:hover td {
        background: var(--v3-surface-2);
    }

    tbody tr:hover .role-compare-checkbox {
        opacity: 1;
    }

    .role--current td {
        background: color-mix(in srgb, var(--v3-accent) 7%, var(--v3-surface));
    }

    .role--current:hover td {
        background: color-mix(in srgb, var(--v3-accent) 7%, var(--v3-surface));
    }
}

/* The CEO ceiling reads as a different thing from an ordinary anchor — solid accent
   rather than an outline, so it is distinguishable at a glance in a dense grid. */
.rc-anchor.is-ceiling {
  background: var(--v3-accent, #2563eb);
  border-color: var(--v3-accent, #2563eb);
  color: var(--v3-accent-fg, #ffffff);
}
</style>
